import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { TenantAccount } from "~/api/Api";
import { useTenantAccount } from "~/hooks/useTenantAccount";

import { InvitationAttributes, InvitationResponse, Site } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import FormLabel from "../login/formLabel/FormLabel";
import MultiSelect from "../mantine/multiSelect/MultiSelect.tsx";
import Select from "../mantine/select/Select";
import Input from "../ui/input/Input";
import Modal from "../ui/modal/Modal";
import styles from "./TeamMembersTabs.module.scss";

interface InviteTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [admin, setAdmin] = useState<boolean | null>(null);
  const queryClient = useQueryClient();

  const { mutate: inviteMember, isPending } = useMutation<
    InvitationResponse,
    unknown,
    InvitationAttributes
  >({
    mutationFn: async (data: InvitationAttributes) => {
      const response = await apiClient.invitations.invitationsCreate(data);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pendingTeamMembers"],
      });
      enqueueSnackbar("Invitation sent", {
        variant: "success",
      });
      reset();
      onClose();
      setAdmin(null);
    },
    onError: (error) => {
      const errorMessage =
        // @ts-expect-error BE types
        error?.error?.error?.message || "Error inviting member";

      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
  });

  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await apiClient.sites.sitesList({
        "page[size]": 1000,
        s: "created_at ASC",
      });

      return response.data?.sites || [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: tenantAccountsData, isLoading: isTenantAccountsLoading } =
    useTenantAccount("basic");
  const account = tenantAccountsData?.account;
  const tenantAccounts: TenantAccount[] = account ? [account] : [];
  const hasTenantAccounts = Boolean(account);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<{
    email: string;
    site_ids: string[];
    admin: boolean | null;
    tenant_account_id: string | null;
  }>({
    defaultValues: {
      email: "",
      site_ids: [] as string[],
      admin: null,
      tenant_account_id: null,
    },
  });

  const emailValue = watch("email");
  const siteIdsValue = watch("site_ids");

  const handleFormSubmit = (formData: {
    email: string;
    site_ids: string[];
    admin: boolean | null;
    tenant_account_id: string | null;
  }) => {
    const site_ids = formData.site_ids.includes("all-sites")
      ? []
      : formData.site_ids.map((id) => Number(id));

    const payload: InvitationAttributes = {
      email: formData.email,
      admin: admin ?? undefined,
      site_ids: admin ? [] : site_ids,
      ...(hasTenantAccounts && formData.tenant_account_id
        ? { tenant_account_id: Number(formData.tenant_account_id) }
        : {}),
    };

    inviteMember(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => {
        onClose();
        reset();
        setAdmin(null);
      }}
      isLoading={isPending}
      key="inviteTeamMember"
      buttonLabel="Send Invitation"
      isDisabled={
        admin === null ||
        !emailValue ||
        (admin === false && siteIdsValue.length === 0)
      }
      formId="inviteTeamMember"
      // @ts-expect-error TS2339 non-existing className
      modalLabel={<h3 className={styles.modalTitle}>Add Team Member</h3>}
    >
      <div className={styles.inviteTeamMemberModal}>
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <p className={styles.modalDescription}>
          Choose the role of a future member of your team and pay attention to
          the difference in access in different roles
        </p>
        <div className={styles.roleButtons}>
          <div
            className={clsx(styles.roleButton, {
              [styles.roleButtonActive]: admin === true,
            })}
            role="button"
            onClick={() => setAdmin(true)}
            style={{ borderColor: admin === true ? "#3B82F6" : "#E5E7EB" }}
          >
            <div className={styles.roleButtonHeader}>
              <Icon iconName="AdminIcon" />
              {/*
               // @ts-expect-error TS2551 non-existing className */}
              <div className={styles.roleButtonHeaderTitle}>Admin</div>
            </div>
            <div className={styles.roleButtonHeaderDescription}>
              Admins can access all websites and their settings, view, and edit
              billing information and IP keys. They have complete control over
              managing team members.
            </div>
          </div>
          <div
            className={clsx(styles.roleButton, {
              [styles.roleButtonActive]: admin === false,
            })}
            role="button"
            onClick={() => setAdmin(false)}
            style={{ borderColor: admin === false ? "#3B82F6" : "#E5E7EB" }}
          >
            <div className={styles.roleButtonHeader}>
              <Icon iconName="MemberIcon" />
              {/*
               // @ts-expect-error TS2551 non-existing className */}
              <div className={styles.roleButtonHeaderTitle}>Member</div>
            </div>
            <div className={styles.roleButtonHeaderDescription}>
              Members have limited access to website settings, can view team
              members but cannot edit them.
            </div>
          </div>
        </div>
        {admin === true && (
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            id="inviteTeamMember"
            className={styles.inviteTeamMemberForm}
          >
            <FormLabel label="Email" className={styles.selectContainer} />
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder="Enter email address"
                  errormessage={errors.email?.message}
                  {...field}
                />
              )}
            />
          </form>
        )}
        {admin === false && (
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            id="inviteTeamMember"
            className={styles.inviteTeamMemberForm}
          >
            <FormLabel label="Email" />
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder="Enter team member`s email"
                  errormessage={errors.email?.message}
                  {...field}
                />
              )}
            />

            {hasTenantAccounts && (
              <>
                <FormLabel
                  label="Tenant account"
                  className={styles.selectContainer}
                />
                <Controller
                  name="tenant_account_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      data={[
                        { value: "", label: "Full member (main team)" },
                        ...(tenantAccounts && tenantAccounts.length > 0
                          ? tenantAccounts.map((account: TenantAccount) => ({
                              value: String(account.id),
                              label: `${account.name} (white-label)`,
                            }))
                          : []),
                      ]}
                      placeholder={
                        isTenantAccountsLoading
                          ? "Loading tenant accounts..."
                          : "Select tenant account"
                      }
                      value={field.value ?? ""}
                      onChange={(value) => field.onChange(value)}
                      searchable={false}
                      clearable={false}
                    />
                  )}
                />
              </>
            )}

            <FormLabel label="Site Access" className={styles.selectContainer} />
            <Controller
              name="site_ids"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  multiple
                  data={[
                    { value: "all-sites", label: "All sites" },
                    ...(sites?.map((site: Site) => ({
                      value: String(site?.id),
                      label: site?.fqdn,
                    })) || []),
                  ]}
                  searchable
                  clearable
                  {...field}
                  onChange={(values: string[]) => {
                    if (
                      values.includes("all-sites") &&
                      !field.value.includes("all-sites")
                    ) {
                      field.onChange(["all-sites"]);

                      return;
                    }

                    if (
                      field.value.includes("all-sites") &&
                      values.length > 1
                    ) {
                      const specificSites = values.filter(
                        (value) => value !== "all-sites",
                      );
                      field.onChange(specificSites);

                      return;
                    }

                    field.onChange(values);
                  }}
                />
              )}
            />
          </form>
        )}
      </div>
    </Modal>
  );
};
